/**
 * Banner / card that lives at the top of the dashboard for self-mode users
 * (planning_scope === 'self'). Lets them invite their partner to plan together.
 *
 * Three states:
 *   1. No invite sent → friendly CTA + "Invite my partner" button
 *   2. Pending invite → status pill + Cancel + Resend
 *   3. Accepted invite → brief confetti-y note (only shown briefly; once
 *      planning_scope flips to 'both' the whole banner stops rendering)
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Heart, Mail, Sparks, Xmark, Send } from 'iconoir-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartnerInviteCard({ wedding, onScopeFlipped }) {
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ name: '', email: '' });
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    if (!wedding?.id) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/partner/invite/${wedding.id}`, { withCredentials: true });
      setInvite(data || null);
    } catch (err) {
      // Don't toast — the banner just stays in "no invite" state.
      console.error('Could not load partner invite:', err?.response?.data);
    } finally {
      setLoading(false);
    }
  }, [wedding?.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Only render at all for self-mode plans. Once the partner accepts, the
  // wedding flips to 'both' and this whole component disappears.
  if (!wedding || wedding.planning_scope !== 'self') return null;
  if (loading) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) {
      toast.error("Add your partner's email so we can send the invite.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/partner/invite`,
        { wedding_id: wedding.id, name: form.name.trim(), email: form.email.trim() },
        { withCredentials: true },
      );
      setInvite(data);
      setShowDialog(false);
      setForm({ name: '', email: '' });
      if (data?.status === 'accepted') {
        // Their partner already had an account — scope already flipped server-side.
        toast.success(`${form.name || 'Your partner'} is now planning with you.`);
        onScopeFlipped?.();
      } else {
        toast.success(`Invite sent to ${form.email}. They'll get an email shortly.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send invite.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Cancel the invite to your partner? They won't be notified.")) return;
    try {
      await axios.delete(`${API}/partner/invite/${wedding.id}`, { withCredentials: true });
      setInvite(null);
      toast.success('Invite cancelled.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not cancel invite.');
    }
  };

  const handleResend = async () => {
    try {
      await axios.post(`${API}/partner/invite/${wedding.id}/resend`, {}, { withCredentials: true });
      toast.success('Invite re-sent. Tell them to check spam too just in case.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not resend.');
    }
  };

  // ── Pending state ──
  if (invite && invite.status === 'pending') {
    const label = invite.name?.trim() || invite.email;
    return (
      <Card
        className="card-surface p-5 border-l-4 border-ochre/70 bg-ochre/5"
        data-testid="partner-invite-pending"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-ochre/15 text-ochre flex items-center justify-center flex-shrink-0">
              <Mail className="w-4 h-4" strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-charcoal font-medium">
                Waiting for <span className="text-ochre">{label}</span> to accept
              </p>
              <p className="text-xs text-muted mt-0.5">
                Once they sign up with this email, your plans merge automatically.
              </p>
            </div>
          </div>
          <div className="flex flex-shrink-0 gap-2">
            <Button
              type="button"
              onClick={handleResend}
              className="btn-secondary h-9 text-xs flex items-center gap-1.5"
              data-testid="resend-partner-invite-btn"
            >
              <Send className="w-3.5 h-3.5" strokeWidth={1.5} />
              Resend
            </Button>
            <Button
              type="button"
              onClick={handleCancel}
              className="btn-secondary h-9 text-xs flex items-center gap-1.5"
              data-testid="cancel-partner-invite-btn"
            >
              <Xmark className="w-3.5 h-3.5" strokeWidth={1.5} />
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // ── No invite yet — primary CTA ──
  return (
    <>
      <Card
        className="card-surface p-5 border-l-4 border-terracotta/70 bg-terracotta/5"
        data-testid="partner-invite-cta"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-terracotta/15 text-terracotta flex items-center justify-center flex-shrink-0">
              <Heart className="w-4 h-4" strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-charcoal font-medium">
                Planning solo? Bring your partner in.
              </p>
              <p className="text-xs text-muted mt-0.5 leading-relaxed">
                Send a one-tap invite — when they accept, your plan flips to
                "Both families' events" and you'll plan side-by-side.
              </p>
            </div>
          </div>
          <Button
            type="button"
            onClick={() => setShowDialog(true)}
            className="btn-primary h-9 text-xs flex items-center gap-1.5 flex-shrink-0"
            data-testid="open-invite-partner-dialog"
          >
            <Sparks className="w-3.5 h-3.5" strokeWidth={1.5} />
            Invite my partner
          </Button>
        </div>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-white max-w-md" data-testid="invite-partner-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Invite your partner</DialogTitle>
            <DialogDescription className="text-muted">
              We'll email them a magic link. Once they accept, your plans merge —
              both of you will see every event, vendor, and budget.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted">First name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Arjun"
                className="input-field h-11"
                data-testid="invite-partner-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="partner@example.com"
                className="input-field h-11"
                required
                data-testid="invite-partner-email-input"
              />
            </div>
            <p className="text-[11px] text-muted leading-relaxed">
              Heads up — you'll auto-flip to "Both families' events" mode once they accept,
              so the second-partner step shows up across the app.
            </p>
            <div className="flex gap-2 justify-end pt-1">
              <Button
                type="button"
                onClick={() => setShowDialog(false)}
                className="btn-secondary"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting || !form.email.trim()}
                className="btn-primary flex items-center gap-1.5"
                data-testid="send-partner-invite-btn"
              >
                <Send className="w-4 h-4" strokeWidth={1.5} />
                {submitting ? 'Sending...' : 'Send invite'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
